import { useState } from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '@/components/MarketingLayout';
import { Section, Container } from '@/components/shared/Section';
import { GlassPanel } from '@/components/shared/GlassPanel';
import { AnimatedReveal } from '@/components/shared/AnimatedReveal';
import { SEO } from '@/components/shared/SEO';
import { Check } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';

export default function ResetPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (!supabase) {
        throw new Error('Supabase is not configured yet.');
      }
      await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Unable to send reset link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MarketingLayout footer="none">
      <SEO title="Reset your password" description="Enter your email and we will send you a reset link." canonical="/reset-password" noindex />
      <Section hero className="min-h-screen flex items-center">
        <Container>
          <div className="max-w-md mx-auto">
            <AnimatedReveal>
              <GlassPanel className="p-8 sm:p-10" data-testid="reset-password-page">
                {!submitted ? (
                  <>
                    <h1 className="text-2xl font-display font-bold text-foreground mb-2">Reset your password</h1>
                    <p className="text-sm text-muted-foreground mb-8">Enter your email and we will send you a reset link.</p>

                    {error && (
                      <div className="mb-5 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                        {error}
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div>
                        <label className="block text-xs font-medium text-foreground/70 mb-2">Email</label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@company.com"
                          className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-lime-500/30 transition-colors"
                          data-testid="reset-email-input"
                          required
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3.5 rounded-xl bg-[#CCFF6C] text-black text-sm font-semibold hover:bg-[#D9FF9A] transition-colors shadow-[0_0_20px_rgba(204,255,108,0.15)] disabled:opacity-50"
                        data-testid="reset-submit-btn"
                      >
                        {loading ? 'Sending...' : 'Send reset link'}
                      </button>
                    </form>

                    <div className="mt-6 text-center">
                      <Link to="/login" className="text-sm text-lime-400 hover:text-lime-300 transition-colors" data-testid="reset-login-link">
                        Back to login
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-center mb-6">
                      <div className="h-12 w-12 rounded-full bg-lime-500/10 border border-lime-500/20 flex items-center justify-center">
                        <Check className="h-6 w-6 text-lime-500" />
                      </div>
                    </div>
                    <h1 className="text-2xl font-display font-bold text-foreground mb-2 text-center">Check your email</h1>
                    <p className="text-sm text-muted-foreground text-center mb-8">
                      We sent a password reset link to <span className="text-foreground">{email}</span>
                    </p>
                    <Link to="/login">
                      <button
                        className="w-full py-3.5 rounded-xl bg-[#CCFF6C] text-black text-sm font-semibold hover:bg-[#D9FF9A] transition-colors"
                        data-testid="reset-back-to-login-btn"
                      >
                        Back to sign in
                      </button>
                    </Link>
                  </>
                )}
              </GlassPanel>
            </AnimatedReveal>
          </div>
        </Container>
      </Section>
    </MarketingLayout>
  );
}
